import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
  ReplaceTextValueArgs, StyleByContinuousValueArgs,
  StyleByThresholdsArgs,
  StyleThreshold
} from '../../../../../model/synoptics/synoptic-arg-types';
import { cloneDeep } from 'lodash';
import { Element } from '@svgdotjs/svg.js';
import { SelectItem } from 'primeng/api';
import { DeviceMeasurement } from '../../../../../model/device-measurement';
import { AssetMeasurement } from '../../../../../model/asset-measurement';
import { Kpi } from '../../../../../model/kpi';
import {
  SynopticElementAction,
  SynopticElementModifier,
  SynopticElementState
} from 'src/app/model/synoptics/synoptic-configuration-types';
import { SensorIdentifier } from '../../../../../model/sensor-identifier';
import {
  SynopticsFormEventActionComponent
} from '../synoptics-form-events/synoptics-form-event-action/synoptics-form-event-action.component';
import { translate } from '@ngneat/transloco';
import { take } from 'rxjs/operators';
import { DialogService } from 'primeng/dynamicdialog';
import {
  SynopticsFormStateModifierComponent
} from './synoptics-form-state-modifier/synoptics-form-state-modifier.component';

@Component({
  selector: 'app-synoptics-form-states',
  templateUrl: './synoptics-form-states.component.html',
  styleUrls: ['./synoptics-form-states.component.scss']
})
export class SynopticsFormStatesComponent implements OnInit {

  @Input() states: SynopticElementState[];
  @Input() deviceMeasurements: DeviceMeasurement[];
  @Input() assetMeasurements: AssetMeasurement[];
  @Input() kpis: Kpi[];
  @Input() selection: Element;
  @Input() uuidSensorMap: Map<string, string>;
  @Output() statesChange: EventEmitter<SynopticElementState[]>;
  @Output() changeSelectionById: EventEmitter<string>;

  public stateBeingModified: SynopticElementState;
  public modifierOptions: SelectItem[];

  constructor(private dialogService: DialogService) {
    this.statesChange = new EventEmitter<SynopticElementState[]>();
    this.changeSelectionById = new EventEmitter<string>();
    this.modifierOptions = [
      { label: 'misc.synopticModifierTypes.replaceTextValue', value: 'replaceTextValue' },
      { label: 'misc.synopticModifierTypes.styleByThresholds', value: 'styleByThresholds' },
      { label: 'misc.synopticModifierTypes.styleByContinuousValue', value: 'styleByContinuousValue' }
    ];
  }

  ngOnInit(): void {
  }

  public addNewState(): void {
    const state = this.generateDefaultState(this.selection.id());
    this.states = [...this.states, state];
    this.onStatesChange();
  }

  public duplicateState(state: SynopticElementState): void {
    const duplicated = cloneDeep(state);
    duplicated.id = this.selection?.id() ?? duplicated.id;
    this.states.push(duplicated);
    this.onStatesChange();
  }

  public removeState(state: SynopticElementState): void {
    this.states = this.states.filter(s => s != state);
    this.onStatesChange();
  }

  public startEditingModifier(state: SynopticElementState): void {
    const data = { modifier: state.modifier };

    const ref = this.dialogService.open(SynopticsFormStateModifierComponent, {
      header: translate(state.modifier?.type ? ('misc.synopticModifierTypes.' + state.modifier.type) : 'edit.configuration'),
      style: { width: 'min(700px, 100vw)', maxHeight: '75vh' },
      modal: true,
      data: data
    });

    ref.onClose.pipe(take(1)).subscribe((newModifier: SynopticElementModifier) => {
      if (newModifier) {
        state.modifier = newModifier;
        this.onStatesChange();
      }
    });
  }

  public generateDefaultArgsForModifier(modifierType: string): any {
    switch (modifierType) {
      case 'replaceTextValue':
        return { decimalPlaces: 2, showUnits: true } as ReplaceTextValueArgs;
      case 'styleByThresholds':
        return { thresholds: [], attributes: ['backgroundColor'], originAnchor: 'center center' } as StyleByThresholdsArgs;
      case 'styleByContinuousValue':
        return {
          valueRange: [0, 100],
          backgroundColorRange: ['#333333', '#407ec9'],
          widthPercentageRange: [0, 100],
          heightPercentageRange: [0, 100],
          opacityPercentageRange: [0, 100],
          attributes: ['backgroundColor'],
          originAnchor: 'center center',
        } as StyleByContinuousValueArgs;
    }
  }

  public applySensorIdentifierToState(state: SynopticElementState, sensorIdentifier: SensorIdentifier): void {
    state.sensorIdentifier = sensorIdentifier;
    this.onStatesChange();
  }

  public onStatesChange(): void {
    this.statesChange.emit(this.states);
  }

  private generateDefaultState(elementId: string): SynopticElementState {
    return { id: elementId, sensorIdentifier: undefined, modifier: { type: null, args: {} } };
  }

}
